import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { RedesignButton } from './RedesignButton';
import { cn } from '@/lib/utils';
import { rdEase, rdFadeUp } from './RedesignMotion';

const navLinks = [
  { to: '/features', label: 'Product', key: 'product' as const },
  { to: '/pricing', label: 'Pricing', key: 'pricing' as const },
  { to: '/product/integrations', label: 'Integrations', key: 'integrations' as const },
  { to: '/blog', label: 'Resources', key: 'resources' as const },
  { to: '/partners', label: 'Partners', key: 'partners' as const },
];

interface RedesignNavProps {
  active?: 'product' | 'pricing' | 'integrations' | 'resources' | 'partners' | 'inside';
}

export function RedesignNav({ active }: RedesignNavProps) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  const isActive = (to: string, key: RedesignNavProps['active']) => {
    if (active && key === active) return true;
    if (to === '/blog') return pathname === '/blog' || pathname.startsWith('/blog/');
    if (to === '/features') return pathname === '/features' || pathname === '/inside';
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <motion.nav
      className="site-nav"
      initial={reduce ? false : { opacity: 0, y: -12 }}
      animate={reduce ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: rdEase }}
    >
      <div className="site-nav-inner">
        <Link className="site-brand" to="/" onClick={() => setOpen(false)}>
          <img src="/marketing/vesta-icon.svg" alt="" width={26} height={26} />
          Vesta CFO
        </Link>
        <div className="site-links hidden lg:flex">
          {navLinks.map(({ to, label, key }) => (
            <motion.div key={to} whileHover={reduce ? undefined : { y: -1 }} transition={{ duration: 0.15 }}>
              <Link
                to={to}
                className={cn(isActive(to, key) && 'text-[var(--vesta-navy)] font-semibold')}
              >
                {label}
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="site-nav-right">
          <RedesignButton variant="ghost" to="/auth" className="hidden sm:inline-flex">
            Sign in
          </RedesignButton>
          <RedesignButton variant="primary" to="/auth" className="hidden sm:inline-flex">
            Get started free
          </RedesignButton>
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg text-[var(--vesta-navy)]"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            className="lg:hidden border-t border-[var(--rd-border-2)] px-6 py-4 flex flex-col gap-3 overflow-hidden"
            style={{ background: 'var(--rd-page)' }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: rdEase }}
          >
            {navLinks.map(({ to, label, key }, index) => (
              <motion.div
                key={to}
                variants={rdFadeUp}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.04, duration: 0.35, ease: rdEase }}
              >
                <Link
                  to={to}
                  onClick={() => setOpen(false)}
                  className={cn(
                    'text-sm font-medium py-1 block',
                    isActive(to, key) && 'text-[var(--vesta-navy)] font-semibold',
                  )}
                >
                  {label}
                </Link>
              </motion.div>
            ))}
            <div className="flex flex-col gap-2 pt-2 border-t border-[var(--rd-border-2)]">
              <RedesignButton variant="ghost" to="/auth" onClick={() => setOpen(false)}>
                Sign in
              </RedesignButton>
              <RedesignButton variant="primary" to="/auth" onClick={() => setOpen(false)}>
                Get started free
              </RedesignButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
